import { globalConfirm } from '../../../components/ConfirmDialog';
import React, { useState, useEffect } from 'react';
import api, { getApiErrorMessage } from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { useDecimalFormat } from '../../../contexts/ExpenditureLabelsContext';
import { Save, BarChart2, TrendingUp, Plus } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend } from 'recharts';

const EMPTY = { code: '', description: '', budget: 0, active: true, is_favorite: false, monthly_budget: {} };
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function AccountCodeModal({ open, onClose, segmentNumber, segmentLabel, initialData, onSaved }) {
  const { fmtVal, fmtTotal } = useDecimalFormat();
  const isEdit = !!initialData?.id;
  const [form, setForm] = useState(EMPTY);
  const [stats, setStats] = useState(null);
  const [chartTab, setChartTab] = useState('expense');
  const [saving, setSaving] = useState(false);
  const [monthlyOpen, setMonthlyOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initialData) {
      setForm({ ...EMPTY, ...initialData, monthly_budget: initialData.monthly_budget || {} });
      // Load stats
      api.get(`/api/account-codes/${segmentNumber}/${initialData.code}/stats`)
        .then(r => setStats(r.data))
        .catch(() => {});
    } else {
      setForm({ ...EMPTY });
      setStats(null);
    }
    setChartTab('expense');
  }, [open, initialData, segmentNumber]);

  const f = (field, val) => setForm(p => ({ ...p, [field]: val }));
  const setMonthBudget = (month, val) =>
    setForm(p => ({ ...p, monthly_budget: { ...p.monthly_budget, [month]: parseFloat(val) || 0 } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (isEdit) {
        await api.put(`/api/account-codes/${segmentNumber}/${form.code}`, payload);
        toast.success('Updated');
      } else {
        await api.post(`/api/account-codes/${segmentNumber}`, payload);
        toast.success('Created');
      }
      onSaved();
      onClose();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Save failed')); }
    setSaving(false);
  };

  const deleteVal = async () => {
    if (!await globalConfirm('Delete this account code?')) return;
    try {
      await api.delete(`/api/account-codes/${segmentNumber}/${form.code}`);
      toast.success('Deleted');
      onSaved();
      onClose();
    } catch (err) { if (err?.code !== 'ERR_CANCELED' && err?.response?.status !== 401 && err?.response?.status !== 403) toast.error('Delete failed'); }
  };

  // Chart data
  const expenseData = MONTHS.map((m, i) => ({
    month: m,
    budget: stats?.monthly_data?.[i]?.budget ?? (form.budget ? form.budget / 12 : 0),
    spent: stats?.monthly_data?.[i]?.spent ?? 0,
  }));

  const runRateData = MONTHS.map((m, i) => {
    const budgetedRunRate = stats?.budgeted_run_rate?.[i] ?? 0;
    const monthlyBudget = stats?.monthly_data?.[i]?.budget ?? (form.budget ? form.budget / 12 : 0);
    return {
      month: m,
      budgetedRunRate: parseFloat(budgetedRunRate.toFixed(2)),
      monthlyBudget: parseFloat(monthlyBudget.toFixed(2)),
      actualSpent: 0,
      avgSpentRunRate: 0,
    };
  });

  const spent = stats?.spent_this_fy ?? 0;
  const totalBudget = form.budget || 0;
  const utilPct = totalBudget > 0 ? Math.min(100, (spent / totalBudget) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto p-0">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-base font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
            {isEdit ? `Edit ${segmentLabel} - ${form.code}` : `Add ${segmentLabel}`}
          </h2>
          <div className="flex items-center gap-2 mr-2">
            <span className="text-sm text-muted-foreground">Active</span>
            <Switch checked={form.active} onCheckedChange={v => f('active', v)} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-0 min-h-[500px]">
          {/* LEFT — Budget Details */}
          <div className="px-6 py-5 space-y-5 border-r">
            {/* Budget Details card */}
            <div className="border rounded-xl p-4 space-y-4">
              <h3 className="font-semibold text-sm">Budget Details</h3>
              <div className="space-y-1.5">
                <Label className="text-primary text-xs font-semibold uppercase tracking-wide">{segmentLabel}</Label>
                <Input
                  value={form.code}
                  onChange={e => f('code', e.target.value)}
                  placeholder={`e.g. GB001`}
                  disabled={isEdit}
                  className="border-primary"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-primary text-xs font-semibold uppercase tracking-wide">Description</Label>
                <Input
                  value={form.description}
                  onChange={e => f('description', e.target.value)}
                  placeholder="Description"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-primary text-xs font-semibold uppercase tracking-wide">Budget</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.0001"
                    value={form.budget}
                    onChange={e => f('budget', e.target.value)}
                    onBlur={() => f('budget', fmtTotal(form.budget))}
                    className="text-right"
                  />
                  <Button
                    type="button" size="sm" variant="outline"
                    className="flex items-center gap-1 whitespace-nowrap"
                    onClick={() => setMonthlyOpen(!monthlyOpen)}
                  >
                    <Plus className="h-3 w-3" /> Monthly Breakup
                  </Button>
                </div>
              </div>

              {/* Monthly budget breakdown */}
              {monthlyOpen && (
                <div className="border rounded-lg p-3 bg-muted/30">
                  <p className="text-xs font-medium mb-2">Monthly Budget Allocation</p>
                  <div className="grid grid-cols-3 gap-2">
                    {MONTHS.map((m, i) => (
                      <div key={m} className="space-y-0.5">
                        <Label className="text-xs">{m}</Label>
                        <Input
                          type="number" step="0.01"
                          value={form.monthly_budget?.[String(i + 1)] ?? ''}
                          onChange={e => setMonthBudget(String(i + 1), e.target.value)}
                          className="h-7 text-xs"
                          placeholder="0.00"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Budget Progress */}
            <div>
              <h3 className="font-semibold text-sm mb-3">Budget Progress</h3>
              <div className="flex justify-between text-xs mb-1.5">
                <span>Spent: <strong>{fmtTotal(spent)}</strong></span>
                <span>Total Budget: <strong>{fmtTotal(totalBudget)}</strong></span>
              </div>
              <div className="w-full h-3 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${utilPct}%` }}
                  role="progressbar" aria-valuenow={utilPct} aria-valuemin={0} aria-valuemax={100}
                />
              </div>
              <p className="text-xs text-right mt-1 text-muted-foreground">{fmtVal(utilPct)}% Utilized</p>
            </div>

            {/* Budget Overview */}
            <div>
              <h3 className="font-semibold text-sm mb-3">Budget Overview</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="border rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-5 h-5 rounded bg-blue-100 flex items-center justify-center">
                      <svg className="w-3 h-3 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    </div>
                    <p className="text-xs font-semibold">Current Financial Year</p>
                  </div>
                  <p className="text-[10px] text-muted-foreground">({stats?.fy_start} – {stats?.fy_end})</p>
                  <p className="text-lg font-bold mt-1">{fmtTotal(spent)}</p>
                  <p className="text-xs text-muted-foreground">Spent This FY</p>
                </div>
                <div className="border rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-5 h-5 rounded bg-green-100 flex items-center justify-center">
                      <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    </div>
                    <p className="text-xs font-semibold">Overall Spend to Date</p>
                  </div>
                  <p className="text-[10px] text-muted-foreground">Includes previous years</p>
                  <p className="text-lg font-bold mt-1">{fmtTotal(stats?.total_spent_alltime ?? 0)}</p>
                  <p className="text-xs text-muted-foreground">Total Spent All-Time</p>
                </div>
              </div>
              <div className="mt-3 border rounded-xl p-3 space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Total Budget:</span>
                  <span className="font-semibold">{fmtTotal(totalBudget)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Available:</span>
                  <span className="font-semibold text-green-600">{fmtTotal(totalBudget - spent)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — Spent Analysis */}
          <div className="px-6 py-5">
            <h3 className="font-semibold text-sm mb-3">Spent Analysis</h3>

            {/* Chart tabs */}
            <div className="flex gap-1 mb-4 border-b pb-2">
              <button
                onClick={() => setChartTab('expense')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  chartTab === 'expense'
                    ? 'bg-primary text-white'
                    : 'text-muted-foreground hover:bg-accent'
                }`}
                aria-pressed={chartTab === 'expense'}
              >
                <BarChart2 className="h-3.5 w-3.5" /> Expense Chart
              </button>
              <button
                onClick={() => setChartTab('runrate')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  chartTab === 'runrate'
                    ? 'bg-primary text-white'
                    : 'text-muted-foreground hover:bg-accent'
                }`}
                aria-pressed={chartTab === 'runrate'}
              >
                <TrendingUp className="h-3.5 w-3.5" /> Run Rate Chart
              </button>
            </div>

            {/* Expense Chart — bar chart monthly budget vs spent */}
            {chartTab === 'expense' && (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={expenseData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                  <Tooltip
                    formatter={(v, name) => [fmtTotal(v), name]}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="budget" name="Monthly Budget" fill="hsl(var(--chart-1))" radius={[2,2,0,0]} />
                  <Bar dataKey="spent" name="Monthly Spent" fill="hsl(var(--chart-5))" radius={[2,2,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            )}

            {/* Run Rate Chart — line chart: budgeted run rate, monthly budget, actual spent, avg spent run rate */}
            {chartTab === 'runrate' && (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={runRateData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                  <Tooltip formatter={(v, n) => [fmtTotal(v), n]} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="budgetedRunRate" name="Budgeted Run Rate"
                    stroke="hsl(var(--chart-1))" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="monthlyBudget" name="Monthly Budget"
                    stroke="hsl(var(--chart-4))" strokeWidth={1.5} dot={{ r: 4 }} strokeDasharray="4 2" />
                  <Line type="monotone" dataKey="actualSpent" name="Actual Spent"
                    stroke="hsl(214,64%,50%)" strokeWidth={1.5} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="avgSpentRunRate" name="Average Spent Run Rate"
                    stroke="hsl(var(--chart-5))" strokeWidth={1.5} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            )}

            <p className="text-xs text-muted-foreground mt-3">
              Actual spend figures will populate automatically once Purchase Orders are raised against this code.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <Button variant="outline" onClick={onClose}>X Cancel</Button>
          {isEdit && <Button variant="destructive" onClick={deleteVal}>Delete</Button>}
          <Button onClick={save} disabled={saving || !form.code || !form.description}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
